import { useEffect, useMemo, useRef, useState } from "react";
import {
  abrCapConfig,
  classifyStreamError,
  CONSERVATIVE_ABR_CAP,
  drmServers,
  STREAM_FAILURE_MESSAGE,
  type AbrCap,
  type StreamFailure,
} from "./streamPlayback";
import {
  playbackCompleted,
  playbackFailed,
  playbackPlaying,
  playbackRebuffered,
  playbackStarted,
} from "../../lib/playbackTelemetry";
import {
  reconcileLanguages,
  trackOptions,
  type CatalogueLanguages,
  type ManifestTrack,
  type TrackOption,
} from "./player/tracks";
import { reportTrackDisagreements } from "./player/trackReport";
import { clampToBounds } from "./player/seekLadder";
import type {
  PlaybackProgress,
  StreamControls,
  StreamThumbnail,
  StreamTracks,
} from "./player/controls";

/**
 * ONE video surface, for every template and every platform (PLAYER-9).
 *
 * See `streamPlayback.ts` for the reasoning behind the pieces. This file is the
 * lifecycle: attach, configure, load, watch, and - the part that matters most on
 * this hardware - hand the decoder back.
 *
 * ## Decoder discipline, carried over wholesale from signage
 *
 * A hotel panel has a small, pooled set of hardware decoders and does not
 * release one just because React removed a node. Leak a few and the next title
 * gets none at all, which surfaces as a screen that plays three things and then
 * plays nothing until a reboot. So: at most ONE element, `destroy()` on the
 * player, and an explicit `pause` / `removeAttribute("src")` / `load()` on the
 * element on the way out. All three, every time.
 *
 * ## A stall is reported, not silently retried
 *
 * The signage surface learned this expensively: four consecutive "recoveries"
 * built on a single counter each made the wall worse, and the fix was to
 * measure first. So the watchdog here NAMES a stall and hands it to the caller;
 * it does not remount, seek or reload on its own.
 *
 * ## Tracks, seeking and progress are ADDITIVE (TV-5)
 *
 * Everything the picker and the seek bar need arrives through OPTIONAL props,
 * and the original contract - `{url, mintLicence, licence, muted,
 * startAtSeconds, onEnded, onFailed, onPlaying}` plus the `stream-surface-video`
 * / `stream-surface-failure` test ids - is untouched, because signage and the
 * scheme templates already call it that way.
 *
 * Two rules hold that line:
 *
 * - **No new prop is in the load effect's dependency list.** They are read
 *   through refs, exactly as the callbacks already were. A prop that re-ran that
 *   effect would re-acquire a decoder on a parent re-render, which is the leak
 *   this file exists to prevent.
 * - **The handle handed to {@link StreamSurfaceProps.onReady} stays safe after
 *   teardown.** A caller may hold it across an unmount; every method checks the
 *   same `disposed` flag the effect uses and answers neutrally instead of
 *   throwing.
 */

export interface StreamLicence {
  /** Widevine licence URL, token included. Null for clear content. */
  widevineUrl?: string | null;
  /** PlayReady, when an environment mints it. Foyer does not, today. */
  playreadyUrl?: string | null;
}

export interface StreamSurfaceProps {
  /** The manifest to play. */
  url: string;
  /**
   * Mint a licence URL, called on EVERY licence request the CDM makes.
   *
   * This is what removes renewal as a concept: a token is minted at the instant
   * it is used, so a two-hour film, a key rotation and a resumed session are the
   * same case. Omit for clear content.
   */
  mintLicence?: () => Promise<StreamLicence | null>;
  /** The licence to start with, when one is already in hand. */
  licence?: StreamLicence | null;
  muted?: boolean;
  /** Seconds into the title to begin. */
  startAtSeconds?: number;
  onEnded?: () => void;
  /** A named failure, with a sentence already chosen for the screen. */
  onFailed?: (failure: StreamFailure, detail: string) => void;
  /** Playback actually began. Distinct from "the component mounted". */
  onPlaying?: () => void;
  /**
   * The decode ceiling this panel may climb to (PLAYER-4).
   *
   * Defaults to `CONSERVATIVE_ABR_CAP` — the known-good 1080p / 6 Mbps encode —
   * because Shaka will otherwise happily select a rendition the SoC refuses,
   * and a player that picks one is worse than no player. A host that has a
   * recorded verdict for this device class passes `chooseAbrCap({verdict})`.
   */
  abrCap?: AbrCap;
  /**
   * This surface is a PRE-WARMED SPARE, not playback the guest asked for.
   *
   * Zapping keeps one spare alive so a channel change does not pay a full
   * player construction (`_shared/guide`). Its loads must not reach
   * `playbackTelemetry`, or `started - playing` — the silent-gap metric
   * PLAYER-17's dead-player alert rests on — counts every neighbour that was
   * warmed and never watched.
   */
  prewarm?: boolean;

  /* --- TV-5, all optional. Nothing below changes the contract above. ------ */

  /**
   * The handle for the picker and the seek bar, handed over once the manifest
   * has loaded. Safe to hold across an unmount - see the header.
   */
  onReady?: (controls: StreamControls) => void;
  /**
   * The audio and subtitle options, whenever they change - at load, and again
   * on every adaptation. Driven by the MANIFEST, which is the truth for
   * playback (`player/tracks.ts`).
   */
  onTracks?: (tracks: StreamTracks) => void;
  /**
   * The playhead, buffer and seekable window. Fires on `timeupdate` (~4 Hz),
   * and on every transport event so a paused or seeking bar is never stale.
   */
  onProgress?: (progress: PlaybackProgress) => void;
  /**
   * What the CATALOGUE claims this title's languages are.
   *
   * Supplied so the surface can RECONCILE the two sources and report where they
   * disagree. The manifest still drives the picker; this only ever produces a
   * named finding (`player/trackReport.ts`). Omit it and no reconciliation
   * happens at all - a caller with no labels is not a caller with wrong ones.
   */
  catalogueLanguages?: CatalogueLanguages | null;
  /** BCP-47 locale for language NAMES in the picker. Never for matching. */
  locale?: string;
}

/** How long the playhead may stand still, mid-play, before it is a stall. */
const STALL_MS = 10_000;
const WATCHDOG_MS = 1_000;

/**
 * Shaka's surface, as much of it as this file uses.
 *
 * Structural rather than imported: the real types live inside a bundle that is
 * loaded lazily and must not be pulled into the main chunk just to name a
 * method. Everything TV-5 added is optional, so a Shaka build that does not
 * expose it (or a test double that does not bother) degrades to "no tracks, no
 * thumbnails" instead of throwing.
 */
interface ShakaTrack {
  id: number;
  language?: string;
  label?: string | null;
  roles?: string[];
  audioRoles?: string[] | null;
  kind?: string | null;
  channelsCount?: number | null;
  active?: boolean;
  type?: string;
}

interface ShakaThumbnail {
  uris?: string[];
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  sprite?: boolean;
}

interface ShakaPlayer {
  attach(media: HTMLMediaElement): Promise<void>;
  load(uri: string, startTime?: number): Promise<void>;
  destroy(): Promise<void>;
  configure(config: unknown): void;
  addEventListener(type: string, cb: (e: unknown) => void): void;
  getNetworkingEngine(): {
    registerRequestFilter(
      filter: (
        type: number,
        request: { uris: string[] }
      ) => Promise<void> | void
    ): void;
  } | null;
  getVariantTracks?(): ShakaTrack[];
  getTextTracks?(): ShakaTrack[];
  getImageTracks?(): ShakaTrack[];
  selectAudioLanguage?(language: string, role?: string): void;
  selectTextTrack?(track: { id: number }): void;
  setTextTrackVisibility?(visible: boolean): void;
  isTextTrackVisible?(): boolean;
  seekRange?(): { start: number; end: number };
  getThumbnails?(trackId: number, time: number): Promise<ShakaThumbnail | null>;
}

/** Shaka's variant/text track shape → the manifest track `tracks.ts` reads. */
function toManifestTrack(track: ShakaTrack): ManifestTrack {
  return {
    id: track.id,
    language: typeof track.language === "string" ? track.language : "",
    label: track.label ?? null,
    // A variant carries its audio roles under `audioRoles`; a text track under
    // `roles`. Both, because taking one would silently drop commentary tracks
    // on whichever side of the manifest happened not to be checked.
    roles: track.audioRoles ?? track.roles ?? [],
    kind: track.kind ?? null,
    channelsCount: track.channelsCount ?? null,
    active: track.active === true,
  };
}

const NO_TRACKS: StreamTracks = { audio: [], text: [], textVisible: false };

export default function StreamSurface({
  url,
  mintLicence,
  licence = null,
  muted = false,
  startAtSeconds = 0,
  onEnded,
  onFailed,
  onPlaying,
  abrCap = CONSERVATIVE_ABR_CAP,
  prewarm = false,
  onReady,
  onTracks,
  onProgress,
  catalogueLanguages = null,
  locale = "en",
}: StreamSurfaceProps) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [failure, setFailure] = useState<StreamFailure | null>(null);
  // Held in refs so the load effect does not re-run - and re-acquire a decoder -
  // every time a parent re-renders with a new callback identity. Every TV-5
  // prop joins them for exactly the same reason.
  const mintRef = useRef(mintLicence);
  const failedRef = useRef(onFailed);
  const endedRef = useRef(onEnded);
  const playingRef = useRef(onPlaying);
  const readyRef = useRef(onReady);
  const tracksRef = useRef(onTracks);
  const progressRef = useRef(onProgress);
  const catalogueRef = useRef(catalogueLanguages);
  const localeRef = useRef(locale);
  // Both read through refs and deliberately absent from the load effect's
  // dependency list. `prewarm` flips the instant a spare is promoted to the
  // watched channel, and `abrCap` changes when a verdict arrives — either in
  // that array would tear down a loaded player and re-acquire a decoder, which
  // is the one thing pre-warming exists to avoid.
  const prewarmRef = useRef(prewarm);
  const abrCapRef = useRef(abrCap);
  useEffect(() => {
    mintRef.current = mintLicence;
    failedRef.current = onFailed;
    endedRef.current = onEnded;
    playingRef.current = onPlaying;
    readyRef.current = onReady;
    tracksRef.current = onTracks;
    progressRef.current = onProgress;
    catalogueRef.current = catalogueLanguages;
    localeRef.current = locale;
    prewarmRef.current = prewarm;
    abrCapRef.current = abrCap;
  });

  /**
   * The live player, for the handle and the progress effect.
   *
   * Cleared in the SAME cleanup that destroys it, so a handle a caller kept
   * across an unmount finds null here and answers neutrally rather than calling
   * into a destroyed player.
   */
  const playerRef = useRef<ShakaPlayer | null>(null);

  /**
   * The handle, built ONCE and never re-identified.
   *
   * A new object per render would make `onReady` fire on every parent render,
   * and a caller storing it in state would loop. It reads `playerRef` and
   * `ref` on each call instead, which is also what makes it survive a reload
   * of the same surface with a different url.
   */
  const controls = useMemo<StreamControls>(
    () => ({
      tracks: () => readTracks(playerRef.current, localeRef.current),
      selectAudio: (option: TrackOption) => {
        const player = playerRef.current;
        if (!player || typeof player.selectAudioLanguage !== "function") return;
        try {
          // 🚨 The manifest's OWN spelling goes back to the player. We matched
          // case-insensitively because production holds 37 UPPERCASE codes that
          // staging does not; handing the canonicalised code back would put the
          // same mismatch one layer down, inside Shaka's own track index.
          const role = option.note === "Commentary" ? "commentary" : undefined;
          player.selectAudioLanguage(option.rawLanguage, role);
        } catch {
          /* an unselectable track leaves the current one playing */
        }
      },
      selectText: (option: TrackOption | null) => {
        const player = playerRef.current;
        if (!player) return;
        try {
          if (!option) {
            player.setTextTrackVisibility?.(false);
            return;
          }
          const id = option.ids[0];
          // The track OBJECT, not a language string: text has no ABR to
          // disturb, so pinning the exact rendition is both possible and more
          // faithful than asking Shaka to re-resolve a language we already
          // resolved.
          if (typeof id === "number") player.selectTextTrack?.({ id });
          player.setTextTrackVisibility?.(true);
        } catch {
          /* same */
        }
      },
      seekTo: (seconds: number) => {
        const video = ref.current;
        if (!video) return;
        try {
          video.currentTime = clampToBounds(
            seconds,
            seekBounds(video, playerRef.current)
          );
        } catch {
          /* a seek onto an unbuffered edge is the player's to refuse */
        }
      },
      progress: () => readProgress(ref.current, playerRef.current),
      play: () => {
        void ref.current?.play().catch(() => {});
      },
      pause: () => {
        try {
          ref.current?.pause();
        } catch {
          /* nothing to pause */
        }
      },
      togglePlay: () => {
        const video = ref.current;
        if (!video) return true;
        if (video.paused) {
          void video.play().catch(() => {});
          return false;
        }
        try {
          video.pause();
        } catch {
          /* nothing to pause */
        }
        return true;
      },
      thumbnail: (seconds: number) => readThumbnail(playerRef.current, seconds),
    }),
    []
  );

  /**
   * Progress, bound to the ELEMENT rather than to a load.
   *
   * A separate effect with an empty dependency list, on purpose: the `<video>`
   * node is rendered unconditionally and never replaced, so these listeners
   * outlive every url change and never participate in the decoder lifecycle
   * below. `timeupdate` is ~4 Hz, which is all a ten-foot bar needs; the
   * transport events are there so a paused or seeking bar is correct the
   * instant it changes rather than a quarter-second later.
   */
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const emit = () =>
      progressRef.current?.(readProgress(video, playerRef.current));
    const events = [
      "timeupdate",
      "progress",
      "durationchange",
      "seeking",
      "seeked",
      "play",
      "pause",
      "ratechange",
      "loadedmetadata",
    ];
    for (const type of events) video.addEventListener(type, emit);
    emit();
    return () => {
      for (const type of events) video.removeEventListener(type, emit);
    };
  }, []);

  useEffect(() => {
    const video = ref.current;
    if (!video || !url) return;

    let disposed = false;
    let player: ShakaPlayer | null = null;
    let watchdog: ReturnType<typeof setInterval> | null = null;

    const fail = (f: StreamFailure, detail: string) => {
      if (disposed) return;
      // Counted BY CODE (PLAYER-17). A premise-wide `licence_refused` is a key
      // problem somebody must act on today; a single `network_stalled` at 19:00
      // is a hotel's uplink at dinner time. One counter could not tell them
      // apart, and the fleet console would page for both or neither.
      if (!prewarmRef.current) playbackFailed(f);
      setFailure(f);
      failedRef.current?.(f, detail);
    };

    if (!prewarmRef.current) playbackStarted();
    // A rebuffer is the element's own account of running dry. Counted rather
    // than acted on - the stall watchdog below is what decides when running dry
    // has become a fault.
    const onWaiting = () => {
      if (!prewarmRef.current) playbackRebuffered();
    };
    video.addEventListener("waiting", onWaiting);

    void (async () => {
      try {
        const mod = await import("shaka-player/dist/shaka-player.compiled");
        if (disposed) return;
        const lib = (mod as { default?: unknown }).default ?? mod;
        const api = lib as {
          polyfill: { installAll(): void };
          Player: new () => ShakaPlayer;
          net?: { NetworkingEngine?: { RequestType?: { LICENSE?: number } } };
        };
        // Idempotent, and required on an Android WebView: without it Shaka
        // refuses EME/media-source shapes the panel reports non-standardly.
        api.polyfill.installAll();

        const instance = new api.Player();
        player = instance;
        playerRef.current = instance;
        await instance.attach(video);
        if (disposed) return;

        // THE DECODE CEILING (PLAYER-4). Configured before `load()`, so the
        // first rendition Shaka picks is already inside it — an ABR ladder
        // authored for the vendor app's native player will otherwise climb to
        // a rung this SoC refuses, and High@4.2 / 12.3 Mbps was measured to
        // stall after five seconds on a real MediaSuite.
        instance.configure(abrCapConfig(abrCapRef.current));

        const servers = drmServers(licence ?? {});
        if (Object.keys(servers).length > 0) {
          instance.configure({ drm: { servers } });
        }

        // THE RENEWAL MECHANISM, and the reason there is no renewal timer.
        // Every licence request the CDM makes gets a freshly minted URL, so the
        // token in flight is never older than one round trip.
        const licenseType =
          api.net?.NetworkingEngine?.RequestType?.LICENSE ?? 2;
        if (mintRef.current) {
          instance
            .getNetworkingEngine()
            ?.registerRequestFilter(async (type, request) => {
              if (type !== licenseType) return;
              const fresh = await mintRef.current?.();
              const next = drmServers(fresh ?? {});
              const uri =
                next["com.widevine.alpha"] ?? next["com.microsoft.playready"];
              // No fresh token: leave the request alone rather than blanking it.
              // Shaka will fail the request and the classifier will call it a
              // refusal, which is the truth - an empty uri list would surface as
              // an unrecognisable internal error instead.
              if (uri) request.uris = [uri];
            });
        }

        instance.addEventListener("error", (e) => {
          const detail = (e as { detail?: unknown }).detail;
          fail(classifyStreamError(detail), describe(detail));
        });

        // Tracks, and the reconciliation, on every change - not only at load.
        // A manifest with a late period or a mid-film adaptation can gain and
        // lose renditions, and a menu built once at load would offer a track
        // that has since gone away.
        const emitTracks = () => {
          if (disposed) return;
          const next = readTracks(instance, localeRef.current);
          tracksRef.current?.(next);
          // The disagreement, reported by name rather than swallowed. Repeats
          // are deduped inside the reporter, so re-emitting on adaptation is
          // free.
          const catalogue = catalogueRef.current;
          if (catalogue) {
            reportTrackDisagreements(
              reconcileLanguages(next.audio, next.text, catalogue),
              catalogue.assetId
            );
          }
        };
        for (const type of ["trackschanged", "variantchanged", "textchanged"]) {
          instance.addEventListener(type, emitTracks);
        }

        await instance.load(
          url,
          startAtSeconds > 0 ? startAtSeconds : undefined
        );
        if (disposed) return;
        emitTracks();
        readyRef.current?.(controls);
        progressRef.current?.(readProgress(video, instance));
        // `started` minus `playing` is the silent gap - loads that never
        // reached a frame and produced no error either. That difference is the
        // failure mode nothing else in the platform can currently see.
        if (!prewarmRef.current) playbackPlaying();
        playingRef.current?.();

        // Only a playhead that is frozen while the element believes it is
        // playing counts. Paused, seeking and ended all legitimately stand
        // still, and judging them would make the watchdog the thing that breaks
        // playback.
        let last = -1;
        let lastMoved = Date.now();
        watchdog = setInterval(() => {
          if (video.paused || video.seeking || video.ended) {
            lastMoved = Date.now();
            return;
          }
          if (video.currentTime !== last) {
            last = video.currentTime;
            lastMoved = Date.now();
            return;
          }
          if (Date.now() - lastMoved >= STALL_MS) {
            if (watchdog) clearInterval(watchdog);
            watchdog = null;
            fail(
              "network_stalled",
              `playhead frozen ${STALL_MS}ms at ${video.currentTime.toFixed(1)}s`
            );
          }
        }, WATCHDOG_MS);
      } catch (e) {
        fail(classifyStreamError(e), describe(e));
      }
    })();

    return () => {
      disposed = true;
      video.removeEventListener("waiting", onWaiting);
      if (watchdog) clearInterval(watchdog);
      // Cleared BEFORE destroy, not after: a handle a caller kept must find
      // null here rather than race a player mid-teardown.
      if (playerRef.current === player) playerRef.current = null;
      // Hand the decoder back, all three steps. `destroy()` alone leaves the
      // element holding a source on some WebViews, and the element alone leaves
      // Shaka's buffers allocated.
      void player?.destroy().catch(() => {});
      try {
        video.pause();
        if (video.readyState !== 0 || video.currentSrc) {
          video.removeAttribute("src");
          video.load();
        }
      } catch {
        /* cleanup must never throw */
      }
    };
    // `controls` is a `useMemo` with an empty dependency list - one object for
    // the life of the component - so listing it would change nothing except to
    // put a second name in front of the three that MUST be here. This effect
    // acquires a hardware decoder; anything added to this array is a decoder
    // re-acquired on a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, licence, startAtSeconds]);

  return (
    <div style={{ position: "absolute", inset: 0, background: "#000" }}>
      <video
        ref={ref}
        autoPlay
        muted={muted}
        playsInline
        onEnded={() => {
          if (!prewarmRef.current) playbackCompleted();
          endedRef.current?.();
        }}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
        data-testid="stream-surface-video"
      />
      {failure && (
        // A sentence, not a spinner. Whoever is standing in front of this needs
        // to know whether to wait, press play again, or call someone.
        <div
          data-testid="stream-surface-failure"
          data-failure={failure}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 8vw",
            textAlign: "center",
            font: "500 2.2vw/1.4 ui-sans-serif, system-ui, sans-serif",
            color: "rgba(255,255,255,.92)",
            background: "rgba(0,0,0,.72)",
          }}
        >
          {STREAM_FAILURE_MESSAGE[failure]}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Reading the player (TV-5). All of it defensive, none of it throwing.        */
/* -------------------------------------------------------------------------- */

/**
 * The audio and text options, from the MANIFEST.
 *
 * Answers empty rather than throwing for every reason it might not work: no
 * player yet, a Shaka build without the method, a manifest still loading. A
 * picker with no rows is a picker a caller can decline to open; an exception
 * here would be thrown from inside a key handler.
 */
function readTracks(player: ShakaPlayer | null, locale: string): StreamTracks {
  if (!player) return NO_TRACKS;
  try {
    const variants = player.getVariantTracks?.() ?? [];
    const texts = player.getTextTracks?.() ?? [];
    return {
      audio: trackOptions(variants.map(toManifestTrack), "audio", locale),
      text: trackOptions(texts.map(toManifestTrack), "text", locale),
      textVisible: player.isTextTrackVisible?.() === true,
    };
  } catch {
    return NO_TRACKS;
  }
}

/**
 * What the guest may seek to.
 *
 * `seekRange()` rather than `[0, duration]` because they are not the same
 * thing on a live or DVR manifest: seeking to `duration` on a live edge is a
 * seek past the end, and a bar drawn against a duration of `Infinity` renders
 * as a zero-width nothing. VOD falls back to the element's own duration, which
 * is what Shaka reports there anyway.
 */
function seekBounds(
  video: HTMLVideoElement | null,
  player: ShakaPlayer | null
): { start: number; end: number } {
  try {
    const range = player?.seekRange?.();
    if (
      range &&
      Number.isFinite(range.start) &&
      Number.isFinite(range.end) &&
      range.end > range.start
    ) {
      return { start: range.start, end: range.end };
    }
  } catch {
    /* fall through to the element */
  }
  const duration = video?.duration ?? 0;
  return {
    start: 0,
    end: Number.isFinite(duration) && duration > 0 ? duration : 0,
  };
}

function readProgress(
  video: HTMLVideoElement | null,
  player: ShakaPlayer | null
): PlaybackProgress {
  if (!video) {
    return {
      currentTime: 0,
      duration: 0,
      bufferedTo: 0,
      bounds: { start: 0, end: 0 },
      paused: true,
      seeking: false,
    };
  }
  const currentTime = Number.isFinite(video.currentTime)
    ? video.currentTime
    : 0;
  // The range CONTAINING the playhead, not the last one. A guest who seeks
  // backwards leaves a forward range behind, and drawing that as "buffered"
  // would promise a smooth scrub through bytes that are no longer ahead.
  let bufferedTo = currentTime;
  try {
    const ranges = video.buffered;
    for (let i = 0; i < ranges.length; i++) {
      if (
        ranges.start(i) <= currentTime + 0.5 &&
        ranges.end(i) >= currentTime
      ) {
        bufferedTo = Math.max(bufferedTo, ranges.end(i));
      }
    }
  } catch {
    /* `buffered` throws on a detached element in some engines */
  }
  return {
    currentTime,
    // `Infinity` is passed THROUGH: it is the honest answer for a live stream
    // and the contract `PlaybackProgress` states. Only NaN - the value before
    // `loadedmetadata` - becomes 0, because "not known yet" and "endless" are
    // different facts and a bar that conflates them draws a live edge as a
    // finished film.
    duration: Number.isNaN(video.duration) ? 0 : video.duration,
    bufferedTo,
    bounds: seekBounds(video, player),
    paused: video.paused,
    seeking: video.seeking,
  };
}

/**
 * The trick-play thumbnail nearest a position, when the manifest has one.
 *
 * Most do not, and that is the expected answer rather than a degraded one -
 * `getImageTracks()` is empty on every clear DASH manifest Foyer packages
 * today. The seek bar therefore treats a thumbnail as decoration it may not
 * get, never as the thing that tells a guest where they are.
 */
async function readThumbnail(
  player: ShakaPlayer | null,
  seconds: number
): Promise<StreamThumbnail | null> {
  if (!player) return null;
  try {
    const track = (player.getImageTracks?.() ?? [])[0];
    if (!track || typeof player.getThumbnails !== "function") return null;
    const thumb = await player.getThumbnails(track.id, seconds);
    const url = thumb?.uris?.[0];
    if (!thumb || !url) return null;
    return {
      url,
      sprite: thumb.sprite === true,
      x: thumb.positionX ?? 0,
      y: thumb.positionY ?? 0,
      width: thumb.width ?? 0,
      height: thumb.height ?? 0,
    };
  } catch {
    return null;
  }
}

/** A short, non-throwing description for telemetry. Never shown to a guest. */
function describe(e: unknown): string {
  if (e && typeof e === "object") {
    const err = e as { code?: number; category?: number; message?: string };
    if (typeof err.code === "number") {
      return `shaka ${err.category ?? "?"}.${err.code}`;
    }
    if (err.message) return err.message.slice(0, 120);
  }
  return String(e).slice(0, 120);
}
